import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Sparkles, ArrowLeft, TrendingUp, AlertCircle, CheckCircle2,
  Loader2, FileText, DollarSign, Send, RefreshCcw, Save, Download,
  Share2, Copy, ExternalLink, ShieldCheck, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtUSD = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(n || 0);

/**
 * Phase 4 — Tax Savings Engine landing page.
 * Loads /api/ocr/{job_id}/tax-savings (POST is idempotent — cached on the
 * job after first run) and renders a magic "you can save $X" hero,
 * deduction cards, errors, YoY swings, and a CTA to email the report
 * to a CPA.
 */
export default function TaxSavingsReport() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const generate = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/tax-savings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to generate tax report.');
      }
      const data = await r.json();
      setReport(data);
    } catch (e) {
      setError(e.message || 'Tax analysis failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // When deep-linked via the SmartActions "Compare to last year" chip
  // (which appends #yoy-swings), scroll the YoY section into view once
  // the report finishes loading.
  useEffect(() => {
    if (!report) return;
    if (typeof window === 'undefined') return;
    const hash = (window.location.hash || '').replace('#', '');
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(t);
  }, [report]);

  const emailToCPA = () => {
    if (!report) return;
    const subject = encodeURIComponent('AI Buck — Tax Savings Review');
    const body = encodeURIComponent(
      [
        `Headline: ${report.headline}`,
        `Estimated savings: ${fmtUSD(report.estimated_total_savings_usd)}`,
        '',
        'Missed deductions:',
        ...(report.missed_deductions || []).map(
          (m) => `• ${m.name} — ${fmtUSD(m.estimated_savings_usd)} (${m.irs_reference || ''})`,
        ),
        '',
        'Errors flagged:',
        ...(report.errors || []).map((e) => `• Line ${e.line}: ${e.issue}`),
        '',
        '— Generated by AI Buck (CountBucks)',
      ].join('\n'),
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    toast.success('Opening your email — paste your CPA\'s address and send.');
  };

  // Save the underlying tax-return OCR job to the Tax Folder via the
  // existing /api/ocr/{id}/file endpoint. The report payload itself is
  // already cached on the job, so once filed the firm can find it under
  // Documents Hub → Tax with the report still attached.
  const [saving, setSaving] = useState(false);
  const saveToTaxFolder = async () => {
    if (!jobId) return;
    setSaving(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destination: 'tax_folder' }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save to Tax Folder.');
      }
      const result = await r.json();
      toast.success(result.message || 'Saved to Tax Folder');
    } catch (e) {
      toast.error(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // Download as a real PDF (was a .txt blob). The backend renders the
  // cached tax-savings report via reportlab and streams the PDF.
  const downloadReport = async () => {
    if (!jobId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/tax-savings.pdf`);
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to generate PDF.');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tax-savings-${jobId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Tax savings report PDF downloaded');
    } catch (e) {
      toast.error(e.message || 'Download failed');
    }
  };

  // ─── Share with client (Phase 2 — client tax-savings viewer) ─────
  // Mints a public /ts/{token} URL that the client can open without logging
  // in. Tracks view count + acknowledgment so the CPA can see engagement.
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareStatus, setShareStatus] = useState(null); // {shared, token, public_url, view_count, acknowledged_at, ...}
  const [shareForm, setShareForm] = useState({ client_email: '', client_name: '', message: '' });
  const [sharing, setSharing] = useState(false);
  const [copiedShareLink, setCopiedShareLink] = useState(false);

  const loadShareStatus = async () => {
    if (!jobId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/share-tax-savings`);
      if (r.ok) setShareStatus(await r.json());
    } catch {
      // silent — share is an optional surface
    }
  };
  useEffect(() => {
    if (jobId) loadShareStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const createOrSendShare = async () => {
    setSharing(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/share-tax-savings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_email: shareForm.client_email || null,
          client_name: shareForm.client_name || null,
          message: shareForm.message || null,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not create share link.');
      }
      const d = await r.json();
      toast.success(
        shareForm.client_email
          ? `Link sent to ${shareForm.client_email}.`
          : 'Share link created — copy and send when ready.',
      );
      await loadShareStatus();
      // Auto-copy link for convenience
      try {
        await navigator.clipboard.writeText(d.public_url);
        setCopiedShareLink(true);
        setTimeout(() => setCopiedShareLink(false), 2500);
      } catch { /* clipboard not available */ }
    } catch (e) {
      toast.error(e.message || 'Share failed.');
    } finally {
      setSharing(false);
    }
  };

  const revokeShare = async () => {
    setSharing(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/share-tax-savings`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error('Could not revoke link.');
      toast.success('Share link revoked. The client will see a "link revoked" page.');
      setShareStatus({ shared: false });
    } catch (e) {
      toast.error(e.message || 'Revoke failed.');
    } finally {
      setSharing(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareStatus?.public_url) return;
    try {
      await navigator.clipboard.writeText(shareStatus.public_url);
      setCopiedShareLink(true);
      toast.success('Link copied to clipboard.');
      setTimeout(() => setCopiedShareLink(false), 2500);
    } catch {
      toast.error('Could not copy link — copy manually.');
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center p-12" data-testid="tax-report-loading">
        <div className="text-center space-y-4 max-w-md">
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-violet-600" />
          <h2 className="text-2xl font-semibold">AI Buck is analyzing your return…</h2>
          <p className="text-sm text-muted-foreground">
            Claude Sonnet 4.5 is reviewing every line for missed deductions, errors, and YoY swings.
            This usually takes 10–30 seconds.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertTitle>Couldn&apos;t generate the report</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="tax-report-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Go back
          </Button>
          <Button onClick={() => generate(true)} data-testid="tax-report-retry">
            <RefreshCcw className="w-4 h-4 mr-2" /> Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const totalSavings = report.estimated_total_savings_usd || 0;

  return (
    <div className="h-full overflow-auto" data-testid="tax-savings-report-page">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="tax-report-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            <h1 className="text-xl font-semibold">Tax Savings Review</h1>
          </div>
          <Badge variant="secondary" className="ml-auto">
            Claude Sonnet 4.5
          </Badge>
        </div>

        {/* Hero */}
        <Card className="border-2 border-emerald-200 dark:border-emerald-900 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
          <CardContent className="p-8 text-center space-y-3">
            <div className="text-sm uppercase tracking-wide text-emerald-700 dark:text-emerald-300 font-semibold">
              AI Buck found potential tax savings of
            </div>
            <div className="text-6xl font-bold text-emerald-700 dark:text-emerald-300" data-testid="tax-report-savings-hero">
              {fmtUSD(totalSavings)}
            </div>
            <p className="text-sm max-w-2xl mx-auto text-muted-foreground">
              {report.headline}
            </p>
            <div className="flex flex-wrap gap-2 justify-center pt-2">
              <Button onClick={saveToTaxFolder} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="tax-report-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save to Tax Folder
              </Button>
              <Button onClick={emailToCPA} variant="outline" data-testid="tax-report-email-cpa">
                <Send className="w-4 h-4 mr-2" /> Email to CPA
              </Button>
              <Button
                onClick={() => setShareDialogOpen(true)}
                variant="outline"
                className={shareStatus?.shared ? 'border-violet-300 text-violet-700' : ''}
                data-testid="tax-report-share-client"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {shareStatus?.shared ? 'Manage client link' : 'Share with client'}
              </Button>
              <Button onClick={downloadReport} variant="outline" data-testid="tax-report-download">
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
              <Button variant="ghost" onClick={() => generate(true)} data-testid="tax-report-regenerate">
                <RefreshCcw className="w-4 h-4 mr-2" /> Re-run
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Missed deductions */}
        {report.missed_deductions?.length > 0 && (
          <Card data-testid="tax-report-deductions">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                Missed deductions ({report.missed_deductions.length})
              </CardTitle>
              <CardDescription>Each one is a real tax-code citation — review with your CPA before filing.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {report.missed_deductions.map((d, i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg border bg-card hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors"
                  data-testid={`tax-deduction-${i}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-semibold">{d.name}</div>
                      <p className="text-sm text-muted-foreground mt-1">{d.description}</p>
                      {d.irs_reference && (
                        <div className="text-xs text-muted-foreground mt-2 font-mono">
                          📎 {d.irs_reference}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-emerald-600">
                        {fmtUSD(d.estimated_savings_usd)}
                      </div>
                      <div className="text-xs text-muted-foreground">est. savings</div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Errors */}
        {report.errors?.length > 0 && (
          <Card data-testid="tax-report-errors">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-rose-600" />
                Potential errors ({report.errors.length})
              </CardTitle>
              <CardDescription>Issues AI Buck flagged for double-checking.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {report.errors.map((e, i) => (
                <Alert key={i} className="border-rose-200 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-900" data-testid={`tax-error-${i}`}>
                  <AlertCircle className="w-4 h-4 text-rose-600" />
                  <AlertTitle>Line {e.line}: {e.issue}</AlertTitle>
                  <AlertDescription>{e.fix}</AlertDescription>
                </Alert>
              ))}
            </CardContent>
          </Card>
        )}

        {/* YoY swings */}
        {report.yoy_swings?.length > 0 && (
          <Card data-testid="tax-report-yoy" id="yoy-swings">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-600" />
                Year-over-year movements ({report.yoy_swings.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {report.yoy_swings.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`tax-yoy-${i}`}>
                  <div>
                    <div className="font-medium">{s.metric}</div>
                    <div className="text-sm text-muted-foreground">{s.commentary}</div>
                  </div>
                  <Badge variant={s.delta_pct > 0 ? 'default' : 'secondary'} className="ml-2">
                    {s.delta_pct > 0 ? '+' : ''}{s.delta_pct?.toFixed?.(1) ?? s.delta_pct}%
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Next steps */}
        {report.next_steps?.length > 0 && (
          <Card data-testid="tax-report-next-steps">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-violet-600" />
                Recommended next steps
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-2 text-sm list-decimal list-inside marker:text-violet-600 marker:font-semibold">
                {report.next_steps.map((s, i) => (
                  <li key={i} data-testid={`tax-next-step-${i}`}>{s}</li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}

        <div className="text-xs text-center text-muted-foreground py-4">
          <FileText className="w-3 h-3 inline mr-1" />
          This is an AI-generated review — not legal/tax advice. Always confirm with a licensed CPA before filing.
        </div>
      </div>

      {/* Share-with-client dialog — Phase 2 client tax-savings viewer */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="share-tax-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-violet-600" />
              Share this review with your client
            </DialogTitle>
            <DialogDescription>
              Generate a branded, read-only link your client can open without logging in.
              They can acknowledge once they&apos;ve reviewed it — you&apos;ll get a notification.
            </DialogDescription>
          </DialogHeader>

          {shareStatus?.shared ? (
            <div className="space-y-4">
              <div className="rounded-lg border bg-violet-50 dark:bg-violet-950/30 p-3 space-y-2">
                <div className="text-xs uppercase tracking-wide text-violet-700 dark:text-violet-300 font-semibold">
                  Active share link
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={shareStatus.public_url}
                    readOnly
                    className="text-xs font-mono bg-white"
                    data-testid="share-link-url"
                  />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={copyShareLink}
                    data-testid="share-link-copy"
                    title="Copy link"
                  >
                    {copiedShareLink ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => window.open(shareStatus.public_url, '_blank', 'noopener')}
                    title="Open in new tab"
                    data-testid="share-link-open"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3 pt-2 text-xs">
                  <div>
                    <div className="text-muted-foreground">Views</div>
                    <div className="font-semibold text-base" data-testid="share-link-views">
                      {shareStatus.view_count ?? 0}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Status</div>
                    <div className="font-semibold text-base">
                      {shareStatus.acknowledged_at ? (
                        <span className="text-emerald-600 flex items-center gap-1">
                          <ShieldCheck className="w-3.5 h-3.5" /> Acknowledged
                        </span>
                      ) : shareStatus.view_count > 0 ? (
                        <span className="text-amber-600">Viewed</span>
                      ) : (
                        <span className="text-muted-foreground">Unopened</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Shared</div>
                    <div className="font-semibold text-base">
                      {shareStatus.shared_at
                        ? new Date(shareStatus.shared_at).toLocaleDateString()
                        : '—'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="share-resend-email">Re-send to client (optional)</Label>
                <Input
                  id="share-resend-email"
                  type="email"
                  placeholder={shareStatus.client_email || 'client@example.com'}
                  value={shareForm.client_email}
                  onChange={(e) => setShareForm((f) => ({ ...f, client_email: e.target.value }))}
                  data-testid="share-resend-email"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
                <Button
                  variant="outline"
                  className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  onClick={revokeShare}
                  disabled={sharing}
                  data-testid="share-link-revoke"
                >
                  {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Revoke link
                </Button>
                <Button
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={createOrSendShare}
                  disabled={sharing || !shareForm.client_email}
                  data-testid="share-link-resend"
                >
                  {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Re-send email
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="share-client-name">Client name (optional)</Label>
                  <Input
                    id="share-client-name"
                    placeholder="Jane Smith"
                    value={shareForm.client_name}
                    onChange={(e) => setShareForm((f) => ({ ...f, client_name: e.target.value }))}
                    data-testid="share-client-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="share-client-email">Client email (optional)</Label>
                  <Input
                    id="share-client-email"
                    type="email"
                    placeholder="jane@example.com"
                    value={shareForm.client_email}
                    onChange={(e) => setShareForm((f) => ({ ...f, client_email: e.target.value }))}
                    data-testid="share-client-email"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="share-message">Personal note (optional)</Label>
                <Textarea
                  id="share-message"
                  rows={3}
                  placeholder="Hi Jane — here's the review we discussed. Let me know if you spot anything…"
                  value={shareForm.message}
                  onChange={(e) => setShareForm((f) => ({ ...f, message: e.target.value }))}
                  data-testid="share-message"
                />
              </div>
              <div className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-3 leading-relaxed">
                <ShieldCheck className="w-3.5 h-3.5 inline mr-1 text-violet-600" />
                Tip: leave the email blank to just generate a copyable link.
                You can revoke this link any time. The page is read-only and uses
                your firm&apos;s logo + brand color from Settings → Workspace.
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={createOrSendShare}
                  disabled={sharing}
                  data-testid="share-create-btn"
                >
                  {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                  {shareForm.client_email ? 'Create link & email client' : 'Create link'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
